<template>
  <div id="app">
    <splash-screen v-if="sessionStorageProcess && (getSplashUrl === 'splash_egy')"></splash-screen>
    <router-view v-else></router-view>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters, mapState } from 'vuex';
import { Howler } from 'howler';
import registerServiceWorkers from '@/common/serviceWorkers';
import SplashScreen from '@/SplashScreen';

export default {
  components: { SplashScreen },
  name: 'app',
  data() {
    return {
      gtmKey: null, // Add key to data
    };
  },
  watch: {
    'userDetails.msisdn': {
      handler() {
        this.loadGoogleAnalyticsScript();
      },
      immediate: false,
    },
  },
  created() {
    this.loadManifest();
    this.getVersionInfo();
    this.enableGTM().then(() => {
      this.loadGoogleAnalyticsScript();
    });
    this.addExternalSEOScript();
    this.updateSoundsStatusFromLocaleStorage();
    this.loadFireBaseScript();
    // Setting global audio volume.
    Howler.volume(0.8);
    registerServiceWorkers();
  },
  computed: {
    ...mapGetters('common', [
      'getSplashUrl',
    ]),
    ...mapGetters('i18n', [
      'getTranslations',
    ]),
    ...mapState('common', [
      'userDetails',
    ]),
    sessionStorageProcess() {
      if (sessionStorage.getItem('splashScreenAlreadyDisplayed') !== 'true' && sessionStorage.getItem('splashScreenProgrammaticallyRefresh') !== 'true') {
        return true;
      }
      sessionStorage.setItem('splashScreenProgrammaticallyRefresh', 'false');
      return false;
    },
  },
  methods: {
    ...mapActions('common', [
      'storeVersionInfo',
    ]),
    ...mapMutations('common', [
      'updateSoundsStatusFromLocaleStorage',
    ]),
    loadManifest() {
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = this.getTranslations.home_manifest_file_path ? `static/${this.getTranslations.home_manifest_file_path}/manifest.json` : 'static/images/favicon/manifest/manifest.json';
        document.head.appendChild(link);
      }
    },
    initFireBase(config) {
      /* eslint-disable */
      if (!firebase.apps.length) {
        console.log('About to initialize FireBase...');
        firebase.initializeApp(JSON.parse(config));
      } else {
        console.log('FireBase is already initialized!');
      }
      /* eslint-enable */
    },
    loadFireBaseScript() {
      this.$axios.get('api/getFireBaseConfigProperties').then((response) => {
        const config = response.data.data;
        if (config) {
          const oldNoScript = document.querySelector('#fireBaseScript');
          if (!oldNoScript) {
            const firebaseScript = document.createElement('script');
            if (firebaseScript.readyState) {
              firebaseScript.onreadystatechange = () => {
                if (firebaseScript.readyState === 'loaded' || firebaseScript.readyState === 'complete') {
                  firebaseScript.onreadystatechange = null;
                  this.initFireBase(config);
                }
              };
            } else firebaseScript.onload = () => this.initFireBase(config);
            firebaseScript.setAttribute('id', 'fireBaseScript');
            firebaseScript.setAttribute('src', 'https://www.gstatic.com/firebasejs/6.2.4/firebase-app.js');
            document.head.insertBefore(firebaseScript, document.head.childNodes[0]);
          }
        }
      });
    },
    getVersionInfo() {
      this.storeVersionInfo();
    },
    enableGTM() {
      return this.$axios('publicapi/getGTMInfo').then((response) => {
        const key = response.data.data;
        if (key) {
          /* eslint-disable */
          // add GMT noscript (no js support case) to body
          this.gtmKey=key;
          const oldNoScript = document.querySelector('#gtmNoScript');
          if (!oldNoScript) {
            const noscript = document.createElement('noscript');
            noscript.setAttribute('id', 'gtmNoScript');
            noscript.innerHTML = `<iframe height="0" width="0" style="display:none;visibility:hidden" src="https://www.googletagmanager.com/ns.html?id=${key}"></iframe>`;
            document.body.insertBefore(noscript, document.body.childNodes[0]);
          }
          // add GMT script to head
          const oldScript = document.querySelector('#gtmScript');
          if (!oldScript) {
            const script = document.createElement('script');
            script.setAttribute('id', 'gtmScript');
            script.innerHTML = "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','" + key + "')";
            document.head.appendChild(script);
          }
          /* eslint-enable */
        }
      });
    },
    loadGoogleAnalyticsScript() {
      const msisdn = this.userDetails.msisdn || undefined;
      console.log('MSISDN:', msisdn);
      console.log('GTM Key:', this.gtmKey);


      if (!document.querySelector('#gtagjsScript')) {
        const gtagjsScript = document.createElement('script');
        gtagjsScript.setAttribute('id', 'gtagjsScript');
        gtagjsScript.async = true;
        gtagjsScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.gtmKey}`;
        document.head.appendChild(gtagjsScript);
      }

      const getGtagScriptConfigCode = `gtag('config', '${this.gtmKey}'${msisdn ? `, {user_id: ${msisdn}}` : ''});`;
      const getGaConfigScript = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
     ${getGtagScriptConfigCode}
      console.log('Google Analytics script loaded successfully with user_id: ${msisdn}');
    `;
      let gaConfigScript = document.querySelector('#gaConfigScript');
      if (gaConfigScript) {
        gaConfigScript.innerHTML = getGaConfigScript;
      } else {
        gaConfigScript = document.createElement('script');
        gaConfigScript.setAttribute('id', 'gaConfigScript');
        gaConfigScript.innerHTML = getGaConfigScript;
        document.head.appendChild(gaConfigScript);
      }
    },
    addExternalSEOScript() {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.innerHTML = '{ "@context" : "http://schema.org", "@type" : "SoftwareApplication", "name" : "Duel Multiplayer Trivia Game", "image" : "https://www.duel.ae/static/images/get_started.png", "url" : "https://www.duel.ae/#/login", "aggregateRating" : { "@type" : "AggregateRating", "ratingValue" : "5" } }';
      document.body.appendChild(script);
    },
  },
};
</script>

<style>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>



// WEBPACK FOOTER //
// src/App.vue